import React, {useEffect, useMemo, useRef} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Flex,
  IconButton,
  Text,
  VStack,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import ViewDetails from '@amblique/king-living/app/components/configuration-selector/view-details';
import {ConfigurationPanelGalleryBackIcon} from '@amblique/king-living/app/components/configuration-selector/configuration-panel-gallery-back-icon';
import {getCoverViewDetailsTitle} from '@amblique/king-living/app/components/complex-customize/option-step/product-picker/cover-variant-tile';
import {Skeleton as ImageGallerySkeleton} from '@amblique/king-living/app/components/image-gallery';
import ImageGalleryV2 from '@amblique/king-living/app/components/image-gallery-v2';
import {useVisualViewportRect} from '@amblique/king-living/app/hooks/use-visual-viewport-rect';
import {resolveGalleryResource} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {getGalleryDetailShortLabel, getViewDetailsDescriptionHtml, getViewDetailsProductTitle} from '@amblique/king-living/app/utils/utils';

const OVERLAY_HEADER_HEIGHT_PX = 60;
const EMPTY_CONFIG_IMAGES = [];

/**
 * Shared gallery + view-details block — used in both desktop left column and
 * mobile full-screen portal overlay.
 *
 * Inline mode (default): renders gallery + view-details directly.
 * Overlay mode (asOverlay=true): wraps in a fixed portal with scroll lock.
 *
 * Does NOT read context — all resolved values come through props.
 *
 * @prop {Object}  masterProduct          - Master product (base for gallery resolution).
 * @prop {Object}  [previewProduct]       - Currently previewed product (highest priority layer).
 * @prop {Object}  [viewDetailsProduct]   - Override for ViewDetails data.
 * @prop {Array}   [layers]               - GalleryLayer[] for option/recommendation products.
 * @prop {Object}  [selections]           - Committed customize selections (cover / options); enables layer priority when set.
 * @prop {boolean} [viewDetailsHideLongDescription] - When true, ViewDetails omits long description (short only).
 * @prop {boolean} [overlayDynamicTitleFromShortDescription] - When true (asOverlay), header uses "{short} details".
 * @prop {string} [overlayCoverItemType] - When set (asOverlay), header uses "{itemType} details" (cover See details).
 * @prop {boolean} [overlayProductDetailHeaderUi] - When true (asOverlay), product short-description header tokens apply.
 */
const ReviewSection = ({
  masterProduct = null,
  previewProduct = null,
  viewDetailsProduct = null,
  galleryResourceOverride = null,
  asOverlay = false,
  desktopLightboxRightInsetPx = 0,
  forceCylindoCheck = false,
  hideImageGalleryOverlayButtons = false,
  imageSize = 'md',
  isOpen = false,
  isSimpleProduct = false,
  layers = [],
  selections = null,
  onClose = null,
  overlayCoverItemType = null,
  overlayDynamicTitleFromShortDescription = false,
  overlayProductDetailHeaderUi = false,
  selectedVariationAttributes = {},
  showViewDetailsSection = true,
  showMobileConfigThumbnails = false,
  isThumbnailZoomEnabled = true,
  imageZoomFullScreen = false,
  stickyGallery = false,
  viewDetailsHideLongDescription = false,
}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('ReviewSection', {});
  const overlayStyles = useMultiStyleConfig('ConfigurationSelector', {});

  const overlayBodyRef = useRef(null);
  const viewportRect = useVisualViewportRect(asOverlay && isOpen);

  const galleryResource = useMemo(() => {
    if (galleryResourceOverride) return galleryResourceOverride;

    return resolveGalleryResource({
      allowCylindo: forceCylindoCheck || !isSimpleProduct,
      layers,
      masterProduct,
      previewProduct,
      selections,
      selectedVariationAttributes,
    });
  }, [
    galleryResourceOverride,
    masterProduct,
    previewProduct,
    selectedVariationAttributes,
    layers,
    selections,
    forceCylindoCheck,
    isSimpleProduct
  ]);

  useEffect(() => {
    if (asOverlay && isOpen && overlayBodyRef.current) {
      overlayBodyRef.current.scrollTop = 0;
    }
  }, [asOverlay, isOpen]);

  useEffect(() => {
    if (!asOverlay || !isOpen || typeof document === 'undefined') return;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('touch-action');
      document.documentElement.style.removeProperty('overflow');
    };
  }, [asOverlay, isOpen]);

  const detailsSource = viewDetailsProduct ?? previewProduct ?? masterProduct;
  const configurationImages = Array.isArray(detailsSource?.c_pimlyConfigurationImage) ?
    detailsSource.c_pimlyConfigurationImage :
    EMPTY_CONFIG_IMAGES;

  const title = useMemo(() => {
    const masterId = masterProduct?.id ?? masterProduct?.productId;
    const sourceId = detailsSource?.id ?? detailsSource?.productId;
    const isChildProduct = Boolean(masterId && sourceId && sourceId !== masterId);

    if (isChildProduct) {
      const coverTitle = getCoverViewDetailsTitle(detailsSource, masterProduct);

      if (coverTitle) return coverTitle;

      return getViewDetailsProductTitle(detailsSource, masterProduct) || detailsSource?.name || '';
    }

    return detailsSource?.displayTitle ?? detailsSource?.name ?? '';
  }, [detailsSource, masterProduct]);
  const galleryReady = galleryResource.isReady;

  const galleryDetailsHeaderLabel = useMemo(
    () => getGalleryDetailShortLabel(detailsSource),
    [detailsSource?.id, detailsSource?.shortDescription, detailsSource?.name]
  );

  const viewDetailsDescriptionHtml = useMemo(
    () => getViewDetailsDescriptionHtml(detailsSource),
    [detailsSource?.id, detailsSource?.shortDescription]
  );

  const overlayHeaderTitle = useMemo(() => {
    if (overlayCoverItemType) {
      return intl.formatMessage(
        {
          defaultMessage: '{label} details',
          id: 'configurationPanel.galleryDetailsTitleWithLabel',
        },
        {label: overlayCoverItemType}
      );
    }

    if (overlayDynamicTitleFromShortDescription && galleryDetailsHeaderLabel) {
      return intl.formatMessage(
        {
          defaultMessage: '{label} details',
          id: 'configurationPanel.galleryDetailsTitleWithLabel',
        },
        {label: galleryDetailsHeaderLabel}
      );
    }

    return intl.formatMessage({
      defaultMessage: 'View layout details',
      id: 'configurationPanel.galleryDetailsTitle',
    });
  }, [overlayCoverItemType, overlayDynamicTitleFromShortDescription, galleryDetailsHeaderLabel, intl]);

  const overlayTitleStyles = overlayCoverItemType ?
    overlayStyles.galleryViewHeaderTitleCoverDetail :
    overlayProductDetailHeaderUi ?
      overlayStyles.galleryViewHeaderTitleProductDetail :
      overlayDynamicTitleFromShortDescription ?
        overlayStyles.galleryViewHeaderTitleCoverDetail :
        overlayStyles.galleryViewHeaderTitle;

  let galleryContent;

  if (!masterProduct) {
    galleryContent = <ImageGallerySkeleton />;
  } else {
    const viewDetails = showViewDetailsSection ? (
      <ViewDetails
        description={viewDetailsDescriptionHtml}
        desktopLightboxRightInsetPx={desktopLightboxRightInsetPx}
        imageZoomFullScreen={imageZoomFullScreen}
        isLoading={false}
        isThumbnailZoomEnabled={isThumbnailZoomEnabled}
        showMobileConfigThumbnails={showMobileConfigThumbnails}
        thumbnail={galleryReady ? configurationImages : []}
        title={title}
        longDescription={
          viewDetailsHideLongDescription ?
            undefined :
            (galleryReady ? detailsSource?.longDescription : undefined)
        }
      />
    ) : null;

    if (!galleryReady) {
      galleryContent = (
        <VStack className="review-section__stack" {...styles.stack}>
          <Box className="configuration-gallery-wrapper" {...styles.galleryWrapper}>
            <ImageGallerySkeleton />
          </Box>
          {viewDetails}
        </VStack>
      );
    } else {
      const effectiveStickyGallery = stickyGallery || asOverlay;

      galleryContent = (
        <VStack className="review-section__stack" {...styles.stack}>
          <Box
            className="configuration-gallery-wrapper"
            {...styles.galleryWrapper}
            {...(effectiveStickyGallery && {
              bg: 'background.primary',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            })}
          >
            <ImageGalleryV2
              customerId={galleryResource.customerId}
              cylindoFeatures={galleryResource.cylindoFeatures}
              cylindoId={galleryResource.cylindoId}
              hideOverlayButtons={hideImageGalleryOverlayButtons}
              lazy={false}
              pimImages={galleryResource.pimImages}
              productName={(previewProduct ?? masterProduct)?.name}
              showArButton={forceCylindoCheck || !isSimpleProduct}
              size={imageSize}
            />
          </Box>
          {viewDetails}
        </VStack>
      );
    }
  }

  const overlayGalleryHeaderDetailClass =
    overlayCoverItemType || overlayProductDetailHeaderUi || overlayDynamicTitleFromShortDescription ?
      'configuration-panel-header--gallery-product-detail' :
      'configuration-panel-header--gallery-layout';

  if (!asOverlay) return galleryContent;

  if (!isOpen || typeof document === 'undefined') return null;

  const hasRect = viewportRect != null;
  const scrollAreaHeight = hasRect ?
    viewportRect.height - OVERLAY_HEADER_HEIGHT_PX :
    `calc(100vh - ${OVERLAY_HEADER_HEIGHT_PX}px)`;

  return createPortal(
    <Box
      className="gallery-panel"
      {...overlayStyles.galleryPanel}
      {...(hasRect ?
        {
          bottom: 'auto',
          height: viewportRect.height,
          left: viewportRect.left,
          maxHeight: viewportRect.height,
          right: 'auto',
          top: viewportRect.top,
          width: viewportRect.width,
        } :
        {height: '100vh', maxHeight: '100vh'}
      )}
    >
      <Flex
        className={[
          'configuration-panel-header',
          'configuration-panel-header--gallery-detail',
          overlayGalleryHeaderDetailClass
        ].join(' ')}
        {...overlayStyles.galleryViewHeader}
        alignItems="center"
        gap={4}
        width="100%"
      >
        <IconButton
          {...overlayStyles.galleryViewBackButton}
          icon={<ConfigurationPanelGalleryBackIcon />}
          aria-label={intl.formatMessage({
            defaultMessage: 'Back to configuration',
            id: 'configurationPanel.aria.backToConfiguration',
          })}
          onClick={onClose}
        />
        {overlayHeaderTitle && (
          <Text {...overlayTitleStyles} noOfLines={1}>
            {overlayHeaderTitle}
          </Text>
        )}
      </Flex>

      <Box
        ref={overlayBodyRef}
        className="gallery-panel-body"
        sx={{
          height: scrollAreaHeight,
          overflowX: 'hidden',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {galleryContent}
      </Box>
    </Box>,
    document.body
  );
};

ReviewSection.displayName = 'ReviewSection';

ReviewSection.propTypes = {
  masterProduct: PropTypes.object,
  previewProduct: PropTypes.object,
  viewDetailsProduct: PropTypes.object,
  galleryResourceOverride: PropTypes.object,
  asOverlay: PropTypes.bool,
  desktopLightboxRightInsetPx: PropTypes.number,
  forceCylindoCheck: PropTypes.bool,
  hideImageGalleryOverlayButtons: PropTypes.bool,
  imageSize: PropTypes.string,
  isOpen: PropTypes.bool,
  isSimpleProduct: PropTypes.bool,
  isThumbnailZoomEnabled: PropTypes.bool,
  imageZoomFullScreen: PropTypes.bool,
  layers: PropTypes.arrayOf(PropTypes.shape({
    cylindoFeature: PropTypes.string,
    pimImages: PropTypes.array,
  })),
  selections: PropTypes.object,
  onClose: PropTypes.func,
  overlayCoverItemType: PropTypes.string,
  overlayDynamicTitleFromShortDescription: PropTypes.bool,
  overlayProductDetailHeaderUi: PropTypes.bool,
  selectedVariationAttributes: PropTypes.object,
  showViewDetailsSection: PropTypes.bool,
  showMobileConfigThumbnails: PropTypes.bool,
  stickyGallery: PropTypes.bool,
  viewDetailsHideLongDescription: PropTypes.bool,
};

export default ReviewSection;
